import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { ErrorBanner } from '@/components/ui';
import { customerBillingService, type CustomerBillingPayment } from '@/api/services/customerBilling';
import { useMe } from '@/context/MeContext';
import { formatApiError } from '@/utils/formatApiError';

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
}

export default function BillingHistoryPage() {
  const { me } = useMe();
  const canView = me?.permissions.canViewCustomerBilling ?? false;
  const [items, setItems] = useState<CustomerBillingPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    customerBillingService
      .payments()
      .then((res) => setItems(res.items))
      .catch((e) => setError(formatApiError(e, 'Failed to load payments')))
      .finally(() => setLoading(false));
  }, [canView]);

  if (!canView) {
    return (
      <>
        <PageHeader title="Payment history" subtitle="Past payments for your account" />
        <p className="muted">You do not have permission to view payment history.</p>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Payment history" subtitle="Past payments for your organization">
        <Link to="/billing" className="btn btn-ghost btn-sm">
          Back to billing
        </Link>
      </PageHeader>
      {error ? <ErrorBanner message={error} /> : null}
      {loading ? <p className="muted">Loading…</p> : null}
      {!loading && items.length === 0 ? <p className="muted">No payments recorded yet.</p> : null}
      <div className="table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Provider</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}</td>
                <td>{item.description || 'Payment'}</td>
                <td>{formatMoney(item.amountCents, item.currency)}</td>
                <td>{item.status}</td>
                <td>{item.provider}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
